import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  REQUEST_TYPE_MAP,
  fetchDocumentTypes,
  fetchHospitals,
  fetchRelationships,
  findOneMedicalAttendance,
  formatSapDate,
  formatSapDateTime,
  getDocuments,
  getEmployeeDisplayName,
  getLoggedInUser,
  getRemarksList,
  getStatusBadgeClass,
  getStatusLabel,
  normalizeDobToDMY,
  resolveDocType,
} from "./medicalAttendance.service";

const NO_DATA = "No data found";

const DisplayMedicalAttendance: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;
  const { referenceNumber } = useParams<{ referenceNumber: string }>();
  const user = getLoggedInUser();

  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [hospitalLabel, setHospitalLabel] = useState("");
  const [diseaseLabel, setDiseaseLabel] = useState("");
  const [docTypeMap, setDocTypeMap] = useState<Record<string, string>>({});
  const [ivfPatientName, setIvfPatientName] = useState("");
  const [patientNamesForRelation, setPatientNamesForRelation] = useState<any[]>([]);

  const refId = referenceNumber ? decodeURIComponent(referenceNumber) : "";

  useEffect(() => {
    fetchDocumentTypes()
      .then(setDocTypeMap)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!refId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await findOneMedicalAttendance(refId);
        setDetail(data);

        const key = data?.HospitalRecordKey;
        const requestType = data?.RequestTypeForMedicalAttendanceRules;
        const requestTypeStr = requestType != null ? String(requestType) : "";

        if (requestTypeStr === "62" || requestTypeStr === "63") {
          setDiseaseLabel(
            data?.DiseaseName ||
              data?.MappedDiseasePara62 ||
              (requestTypeStr === "62"
                ? data?.DiseaseForProlongTreatmentPara62 != null
                  ? String(data.DiseaseForProlongTreatmentPara62)
                  : ""
                : data?.DiseaseForProlongTreatmentPara63 != null
                  ? String(data.DiseaseForProlongTreatmentPara63)
                  : "")
          );
        } else {
          setDiseaseLabel("");
        }

        if (requestTypeStr === "62") {
          const name = data?.HospitalName ? String(data.HospitalName).trim() : "";
          const address = data?.HospitalAddress ? String(data.HospitalAddress).trim() : "";
          if (name && address) setHospitalLabel(`${name} - ${address}`);
          else setHospitalLabel(name || address || (key != null ? String(key) : ""));
        } else if (key && requestType) {
          try {
            const options = await fetchHospitals(String(requestType));
            const opt = options.find((d) => String(d.id) === String(key));
            setHospitalLabel(opt?.label || String(key));
          } catch {
            setHospitalLabel(String(key));
          }
        } else if (data?.HospitalRecordKey != null) {
          setHospitalLabel(String(data.HospitalRecordKey));
        }

        if (requestTypeStr === "66" || requestTypeStr === "62" || requestTypeStr === "63") {
          const empId4Patient = String(data?.EmpID ?? "").trim();
          if (empId4Patient) {
            try {
              const arr = await fetchRelationships(empId4Patient);
              setPatientNamesForRelation(arr);
            } catch {
              /* ignore */
            }
          }
          const ownerEmpId = String(data?.EmpID ?? "").trim();
          const viewerEmpId = String(user?.details?.empId ?? user?.empId ?? "").trim();
          const isOwnRequest =
            ownerEmpId !== "" &&
            viewerEmpId !== "" &&
            ownerEmpId.replace(/^0+/, "") === viewerEmpId.replace(/^0+/, "");
          if (isOwnRequest) {
            const empFullName = getEmployeeDisplayName(user);
            if (empFullName && empFullName !== "-") setIvfPatientName(empFullName);
          }
        }
      } catch (err: any) {
        setDetail(null);
        toast.error(Api.getErrorMessage(err) || t("failed_to_load_details"));
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refId]);

  const createdDate = detail?.created_at
    ? formatSapDate(detail.created_at).replace(/-/g, "/")
    : NO_DATA;
  const maritalDisplay = detail?.MaritalStatus === "M" ? "Married" : detail?.MaritalStatus || NO_DATA;
  const selfPatientName = String(ivfPatientName ?? "").trim();
  const ownerEmpIdNorm = String(detail?.EmpID ?? "").trim().replace(/^0+/, "");
  const viewerEmpIdNorm = String(user?.details?.empId ?? user?.empId ?? "").trim().replace(/^0+/, "");
  const isOwnRequest =
    ownerEmpIdNorm !== "" && viewerEmpIdNorm !== "" && ownerEmpIdNorm === viewerEmpIdNorm;

  const isSelfPatient = (() => {
    const rawRel = String(detail?.RelationToEmployee ?? "").trim().toUpperCase();
    if (rawRel === "SELF" || rawRel === "0") return true;
    const dpn = String(detail?.PatientName ?? "").trim().toLowerCase();
    if (dpn === "self") return true;
    const spn = selfPatientName.toLowerCase();
    if (spn && dpn && dpn === spn) {
      return rawRel === "" || rawRel === "SELF" || rawRel === "0";
    }
    if (isOwnRequest && (rawRel === "" || rawRel === "SELF" || rawRel === "0")) return true;
    return false;
  })();

  const ivfForValue = isSelfPatient ? "SELF" : "SPOUSE";
  const getIVFForLabel = (value?: string) => {
    if (value === "SELF") return t("self") || "Self";
    if (value === "SPOUSE") return t("spouse") || "Spouse";
    return value || NO_DATA;
  };

  const displayHospitalName = (() => {
    const raw =
      hospitalLabel || (detail?.HospitalRecordKey != null ? String(detail.HospitalRecordKey) : "") || "";
    if (!raw) return NO_DATA;
    if (raw.includes(" - ")) return raw.split(" - ")[0].trim() || NO_DATA;
    return raw;
  })();
  const displayHospitalAddress =
    hospitalLabel && hospitalLabel.includes(" - ")
      ? hospitalLabel.split(" - ").slice(1).join(" - ").trim() || NO_DATA
      : NO_DATA;
  const displayDisease = detail?.DiseaseName || diseaseLabel || NO_DATA;

  const displayRelationToEmployee = (() => {
    if (isSelfPatient) return "Self";
    const rawRelation = String(detail?.RelationToEmployee ?? "").trim();
    if (rawRelation === "") return NO_DATA;
    const rawUpper = rawRelation.toUpperCase();
    if (rawUpper === "SELF" || rawRelation === "0") return "Self";
    const match = patientNamesForRelation.find(
      (p: any) => String(p?.Value ?? "").toUpperCase() === rawUpper
    );
    if (match) {
      const matchVal = String(match.Value ?? "").toUpperCase();
      if (matchVal === "SELF" || String(match.Value ?? "") === "0") return "Self";
      const dd = String(match.Ddtext ?? match.DdText ?? match.ddtext ?? "").trim();
      if (dd) return dd;
    }
    return rawRelation || NO_DATA;
  })();

  const profile = user?.details;
  const employeeName = getEmployeeDisplayName(user);
  const employeeDesignation =
    profile?.designation_details?.longText ||
    profile?.sap_department_designation_wings?.designation_name ||
    NO_DATA;
  const employeeDepartment =
    detail?.Department != null && String(detail.Department).trim() !== ""
      ? String(detail.Department).trim()
      : profile?.sap_department_designation_wings?.department_name || NO_DATA;
  const employeePhone =
    detail?.ContactNumber != null && String(detail.ContactNumber).trim() !== ""
      ? String(detail.ContactNumber).trim()
      : profile?.phone != null && String(profile.phone).trim() !== ""
        ? String(profile.phone).trim()
        : NO_DATA;
  const employeeEmail =
    detail?.EmailID != null && String(detail.EmailID).trim() !== ""
      ? String(detail.EmailID).trim()
      : profile?.email != null && String(profile.email).trim() !== ""
        ? String(profile.email).trim()
        : NO_DATA;

  const documents = getDocuments(detail);
  const remarksList = getRemarksList(detail);
  const requestType = detail?.RequestTypeForMedicalAttendanceRules != null
    ? String(detail.RequestTypeForMedicalAttendanceRules)
    : "";
  const isIVF = requestType === "66";
  const isPara62 = requestType === "62";
  const isPara63 = requestType === "63";
  const requestTypeLabel = requestType ? REQUEST_TYPE_MAP[requestType] ?? requestType : NO_DATA;

  const empDobValue = (() => {
    if (ivfForValue !== "SELF") return "";
    const empDob = profile?.birthdate || profile?.dob || profile?.DOB;
    return empDob ? normalizeDobToDMY(empDob) : "";
  })();
  const spouseNameValue =
    ivfForValue === "SPOUSE" ? String(detail?.SpouseName || detail?.PatientName || "").trim() : "";
  const spouseDobRaw =
    ivfForValue === "SPOUSE" ? detail?.SpouseDOB || detail?.SpouseDob || detail?.spouseDob : "";
  const spouseDobValue = spouseDobRaw ? normalizeDobToDMY(spouseDobRaw) : "";
  const govHospitalName = String(detail?.HospitalName ?? "").trim();
  const govHospitalAddress = String(detail?.HospitalAddress ?? "").trim();
  const isGovernmentHospitalCase =
    (displayHospitalName || "").toLowerCase().includes("government hospital") ||
    !!govHospitalName ||
    !!govHospitalAddress;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{t("ivf_request_details")}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.medicalAttendance.list}>{t("prior_approval_for_ivf_para_6_2_6_3_treatment")}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("display_request")}
                </li>
              </ol>
            </nav>
          </div>
          <button className="btn btn-light" onClick={() => navigate(-1)}>
            <i className="ti ti-arrow-left me-1"></i>
            {t("close")}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
          </div>
        ) : !detail ? (
          <div className="alert alert-light border">{NO_DATA}</div>
        ) : (
          <>
            <div className="alert alert-secondary d-flex align-items-center justify-content-between">
              <span className="fw-semibold">{t("request_status")}</span>
              <span className={getStatusBadgeClass(detail?.RequestStatus)}>
                {getStatusLabel(detail?.RequestStatus, t)}
              </span>
            </div>

            <Section title={t("request_information")} icon="ti ti-briefcase">
              <Field label={t("reference_no")} value={detail?.ReferenceNumber || NO_DATA} />
              <Field label={t("request_type")} value={requestTypeLabel} />
              <Field label={t("created_at") || "Created At"} value={createdDate} />
              <Field label={t("employee_id") || "Employee ID"} value={detail?.EmpID || NO_DATA} />
            </Section>

            <Section title={t("employee_details")} icon="ti ti-user">
              <Field label={t("name")} value={employeeName} />
              <Field label={t("designation")} value={employeeDesignation} />
              <Field label={t("department")} value={employeeDepartment} />
              <Field
                label={t("payscale") || "Payscale"}
                value={detail?.PayScale || detail?.Payscale || detail?.pay_scale || profile?.pay_scale || NO_DATA}
              />
              <Field
                label={t("mode_of_recruitment") || "Mode of Recruitment"}
                value={
                  detail?.EmploymentType ||
                  detail?.employmentType ||
                  detail?.ModeOfRecruitment ||
                  user?.employmentType ||
                  profile?.employmentType ||
                  NO_DATA
                }
              />
              <Field
                label={t("employment_status") || "Employment Status"}
                value={
                  detail?.EmpStatus ||
                  detail?.empStatus ||
                  detail?.EmploymentStatus ||
                  profile?.empStatus ||
                  user?.empStatus ||
                  NO_DATA
                }
              />
              <Field label={t("phone_number") || "Phone Number"} value={employeePhone} />
              <Field label={t("email_id") || "Email ID"} value={employeeEmail} />
            </Section>

            <Section title={t("treatment_details")} icon="ti ti-stethoscope">
              {isIVF && (
                <>
                  <Field label={t("IVF_Treatment_For")} value={getIVFForLabel(ivfForValue)} />
                  <Field label={t("marital_status") || "Marital Status"} value={maritalDisplay} />
                  <Field label={t("from_hospital") || "From Hospital"} value={displayHospitalName} />
                  {isGovernmentHospitalCase && (
                    <>
                      <Field label={t("hospital_name")} value={govHospitalName || NO_DATA} />
                      <Field label={t("hospital_address")} value={govHospitalAddress || displayHospitalAddress} />
                    </>
                  )}
                  {ivfForValue === "SELF" && (
                    <Field label={t("dob_employee")} value={empDobValue || NO_DATA} />
                  )}
                  {ivfForValue === "SPOUSE" && (
                    <>
                      <Field label={t("spouse_name")} value={spouseNameValue || NO_DATA} />
                      <Field label={t("spouse_dob")} value={spouseDobValue || NO_DATA} />
                    </>
                  )}
                </>
              )}
              {(isPara62 || isPara63) && (
                <>
                  <Field label={t("patient_name")} value={detail?.PatientName || NO_DATA} />
                  <Field label={t("relation_to_employee")} value={displayRelationToEmployee} />
                  <Field label={t("patient_age")} value={detail?.Age || NO_DATA} />
                  <Field label={t("disease")} value={displayDisease} />
                  <Field label={t("hospital_name")} value={displayHospitalName} />
                  {isPara62 && <Field label={t("hospital_address")} value={displayHospitalAddress} />}
                </>
              )}
              {isPara63 && (
                <>
                  <Field
                    label={t("Treatment_From")}
                    value={
                      detail?.EmpDateOfTreatmentBegin || detail?.DateOfTreatmentBegin
                        ? normalizeDobToDMY(detail.EmpDateOfTreatmentBegin || detail.DateOfTreatmentBegin)
                        : NO_DATA
                    }
                  />
                  <Field
                    label={t("Treatment_Till")}
                    value={
                      detail?.EmpDateOfTreatmentEnd || detail?.DateOfTreatmentEnd
                        ? normalizeDobToDMY(detail.EmpDateOfTreatmentEnd || detail.DateOfTreatmentEnd)
                        : NO_DATA
                    }
                  />
                </>
              )}
            </Section>

            <Section title={t("History")} icon="ti ti-history">
              {remarksList.length > 0 ? (
                remarksList.map((remark: any, index: number) => (
                  <div
                    key={index}
                    className={`py-2 ${index < remarksList.length - 1 ? "border-bottom" : ""}`}
                  >
                    <div className="fw-semibold">
                      {remark?.ApproverName || NO_DATA}
                      {(remark?.ApproverUserID || remark?.ApproverLevel) && (
                        <small className="text-muted ms-2">
                          ({remark?.ApproverUserID || remark?.ApproverLevel})
                        </small>
                      )}
                    </div>
                    <div className="small text-muted">{formatSapDateTime(remark?.TimeStamp)}</div>
                    <div>{remark?.HRemarks?.trim() || NO_DATA}</div>
                  </div>
                ))
              ) : (
                <div className="text-muted">{NO_DATA}</div>
              )}
            </Section>

            {isIVF && (
              <div className="alert alert-warning">
                <strong>{t("mandatory_attachments")}:</strong>
                <div>{t("prescription_failure_conventional")}</div>
              </div>
            )}

            {documents.length > 0 && (
              <Section title={t("attachments") || "Attachments"} icon="ti ti-paperclip">
                {documents.map((doc: any, index: number) => {
                  const url = doc?.url || doc?.URL || doc?.fileUrl;
                  const fileName = doc.FileName || doc.fileName || `Document_${index + 1}.pdf`;
                  return (
                    <div key={doc.id || index} className="border rounded p-2 mb-2">
                      {url ? (
                        <a href={url} target="_blank" rel="noreferrer" className="fw-semibold">
                          <i className="ti ti-file me-1"></i>
                          {fileName}
                        </a>
                      ) : (
                        <span className="fw-semibold">
                          <i className="ti ti-file me-1"></i>
                          {fileName}
                        </span>
                      )}
                      <Field
                        label={t("document_type") || "Document Type"}
                        value={resolveDocType(doc?.TypeOfDocument, docTypeMap)}
                      />
                    </div>
                  );
                })}
              </Section>
            )}

            <Section title={isIVF ? t("declarations_title") : t("Declaration") || "Declaration"} icon="ti ti-checkbox">
              {(isIVF
                ? [t("declaration1"), t("declaration2"), t("declaration3"), t("declaration4")]
                : [t("declaration_line1"), t("declaration_line2")]
              ).map((line, i) => (
                <p key={i} className="mb-1">
                  {i + 1}. {line}
                </p>
              ))}
            </Section>

            <div className="alert alert-success d-flex align-items-center">
              <i className="ti ti-info-circle me-2 fs-18"></i>
              {t("readonly_notice")}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; icon: string; children: React.ReactNode }> = ({
  title,
  icon,
  children,
}) => (
  <div className="card border-0 shadow-sm mb-3">
    <div className="card-header bg-white">
      <h5 className="mb-0 fw-bold">
        <i className={`${icon} me-2`}></i>
        {title}
      </h5>
    </div>
    <div className="card-body">{children}</div>
  </div>
);

const Field: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 mb-2">
    <span className="text-muted fw-semibold">{label}</span>
    <span className="text-dark text-end">{value ?? "-"}</span>
  </div>
);

export default DisplayMedicalAttendance;
